/*
 * <olivida-cart-tester> — keeps the free tester placeholder line in sync.
 *
 * The server decides, this element only executes. snippets/olivida-cart-tester.liquid
 * renders it exclusively while the cart still needs a mutation:
 *   data-add="<variantId>"                 → add the tester line
 *   data-line="<key>" data-quantity="0|1"  → remove it, or reset its quantity
 *
 * The element lives inside the region that every cart re-render replaces, so
 * connectedCallback is the trigger and no observer is needed. After the
 * mutation the freshly rendered HTML contains no element, which is what ends
 * the cycle — idempotency comes from Liquid, not from state kept here.
 */
class OlividaCartTester extends HTMLElement {
  async connectedCallback() {
    if (this.handled || OlividaCartTester.busy || OlividaCartTester.stopped) return;
    this.handled = true;
    OlividaCartTester.busy = true;

    const drawer = this.closest('cart-drawer');
    const cartItems = document.getElementById('main-cart-items');
    const sectionId = drawer ? 'cart-drawer' : (cartItems && cartItems.dataset.id) || 'main-cart-items';

    let data;
    try {
      data = await this.mutate(sectionId);
    } catch (error) {
      OlividaCartTester.stopped = true;
      OlividaCartTester.busy = false;
      return;
    }

    /* /cart/add.js answers 422 without sections when the variant cannot be
       added at all (archived, channel off, tracked stock at 0). Give up for
       this page load instead of retrying on every re-render. */
    if (!data || !data.sections) {
      OlividaCartTester.stopped = true;
      OlividaCartTester.busy = false;
      return;
    }

    /* Release before touching the DOM: the replacement may carry the next
       element, and its connectedCallback fires synchronously during the swap. */
    OlividaCartTester.busy = false;
    /* Announce before rendering: the replacement may carry the next element,
       whose own publish must land after this one so listeners settle on the
       final count. Everything that mirrors the cart without re-rendering it
       (the bottom bar badge) depends on this — the sections rendered below
       only cover the cart region and the header bubble. */
    this.announce(data);
    this.render(data, drawer, sectionId);
  }

  mutate(sectionId) {
    const sections = sectionId + ',cart-icon-bubble';
    const add = this.dataset.add;
    const route = add ? window.routes.cart_add_url : window.routes.cart_change_url;
    const body = add
      ? { items: [{ id: Number(add), quantity: 1 }], sections: sections }
      : { id: this.dataset.line, quantity: Number(this.dataset.quantity), sections: sections };

    return fetch(route + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then((response) => response.json());
  }

  /* /cart/change.js answers with the whole cart, /cart/add.js only with the
     added line — subscribers that need a count fall back to /cart.js when
     item_count is missing. Source name mirrors cart.js's 'cart-items'
     contract: the cart sections are already rendered, do not re-render. */
  announce(data) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'olivida-cart-tester', cartData: data });
  }

  render(data, drawer, sectionId) {
    this.replaceSection('#cart-icon-bubble', data.sections['cart-icon-bubble'], '.shopify-section');

    if (!drawer) {
      this.replaceSection('.olvd-pc', data.sections[sectionId], '.olvd-pc');
      return;
    }

    /* CartDrawer.renderContents() opens the drawer on its own, which must not
       happen on an ordinary page view. Only reuse it while the drawer is
       already open, so Dawn re-applies its focus trap to the fresh DOM. */
    if (drawer.classList.contains('active')) {
      drawer.renderContents(data);
      return;
    }

    this.replaceSection('#CartDrawer', data.sections[sectionId], '#CartDrawer');
    /* #CartDrawer-Overlay is inside the replaced markup, so its close listener
       (bound in the CartDrawer constructor) has to be re-attached. */
    const overlay = document.getElementById('CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
    if (typeof data.item_count === 'number') drawer.classList.toggle('is-empty', data.item_count === 0);
  }

  replaceSection(targetSelector, html, sourceSelector) {
    if (!html) return;
    const target = document.querySelector(targetSelector);
    if (!target) return;
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector(sourceSelector);
    if (source) target.innerHTML = source.innerHTML;
  }
}

OlividaCartTester.busy = false;
OlividaCartTester.stopped = false;

customElements.define('olivida-cart-tester', OlividaCartTester);
